/**
 * API -> view-model adapter for the "USB Topology & Health" section of the
 * Host Detail page. Consumes the `GET /hosts/:id` response verbatim
 * (`src/api/hosts.ts`) — `hubs[].health` is always exactly one of
 * `'ok' | 'warning' | 'serious' | 'unknown'`, never null/undefined.
 *
 * NO POWER: hardware validation proved there is no live power telemetry on
 * this hardware (hubs always report `bus_power_used: 0`, hub/device power is
 * a static advertised descriptor, not a live reading — see
 * src/api/hub-health.ts and src/agent/usb-topology.ts). This adapter must
 * never derive health, KPIs, or view state from bus_power/bus_power_used.
 */

export type HealthView = "good" | "warning" | "critical" | "unknown";

/** Exhaustive map from the API's HubHealth bucket to the dashboard's health
 * vocabulary. Any unmapped/unexpected input becomes 'unknown' — NEVER
 * defaults to 'good'. A stale/unrecognized signal must never read as
 * healthy. */
const HEALTH_MAP: Record<string, HealthView> = {
  ok: "good",
  warning: "warning",
  serious: "critical",
  unknown: "unknown",
};

export function mapHubHealth(raw: string | null | undefined): HealthView {
  if (raw == null) return "unknown";
  return HEALTH_MAP[raw] ?? "unknown";
}

/** Edge/link styling bucket. 'high' = healthy link, 'full' = degraded
 * (below high_speed — full_speed/low_speed), 'na' = offline or no reading. */
export type LinkSpeedBucket = "high" | "full" | "na";

const DEGRADED_LINK_SPEEDS = new Set(["full_speed", "low_speed"]);

export function bucketLinkSpeed(
  linkSpeedRaw: string | null | undefined,
  status: string
): LinkSpeedBucket {
  if (status === "offline") return "na";
  if (!linkSpeedRaw) return "na";
  if (DEGRADED_LINK_SPEEDS.has(linkSpeedRaw)) return "full";
  return "high";
}

/** Mirrors src/agent/usb-topology.ts::MatchConfidence. Optional on the wire
 * today — the API contract doesn't currently ship it, so every consumer
 * must guard on presence rather than assume it exists. */
export type MatchConfidence =
  | "exact"
  | "case_insensitive"
  | "unique_fallback"
  | "unmatched";

const LOW_CONFIDENCE: ReadonlySet<MatchConfidence> = new Set([
  "unique_fallback",
  "unmatched",
]);

/** Shape of one entry in `GET /hosts/:id` response `devices[]`, trimmed to
 * the fields this adapter actually reads. */
export interface ApiTopologyDevice {
  id: string;
  serial: string;
  model: string;
  status: string;
  hub_id: string | null;
  link_speed: string | null;
  match_confidence?: MatchConfidence;
}

/** Shape of one entry in `GET /hosts/:id` response `hubs[]`. */
export interface ApiTopologyHub {
  id: string;
  location_id: string;
  name: string | null;
  uplink_hub_id: string | null;
  top_level_location_id: string | null;
  bus_power: number | null;
  bus_power_used: number | null;
  usb_device_count: number;
  last_seen_at: string | null;
  health: string;
  device_ids: string[];
}

export interface TopologyDevice {
  id: string;
  serial: string;
  model: string;
  status: string;
  hubId: string;
  linkSpeedRaw: string | null;
  linkSpeedBucket: LinkSpeedBucket;
  isOffline: boolean;
  isDegraded: boolean;
  /** True only when match_confidence is present AND is not exact/case_insensitive. */
  lowConfidenceMatch: boolean;
  matchConfidence?: MatchConfidence;
}

export interface TopologyHub {
  id: string;
  name: string;
  locationId: string;
  uplinkHubId: string | null;
  health: HealthView;
  healthRaw: string;
  deviceCount: number;
  deviceIds: string[];
  lastSeenAt: string | null;
}

export interface TopologyKpis {
  deviceCount: number;
  onlineCount: number;
  offlineCount: number;
  hubCount: number;
  degradedLinkCount: number;
}

export interface TopologyViewModel {
  hubs: TopologyHub[];
  devices: TopologyDevice[];
  kpis: TopologyKpis;
}

/**
 * Build the topology view model. Devices with no `hub_id` (not yet resolved
 * onto a physical hub by the agent's USB scan) are excluded from the
 * fan-out graph and KPI counts — there's nowhere on the tree to place them.
 * Always guards `hubs`/`devices` being null/undefined (absent-hubs, loading,
 * or pre-topology-agent hosts) — never throws, returns an empty model.
 */
export function adaptTopology(
  hubs: ApiTopologyHub[] | null | undefined,
  devices: ApiTopologyDevice[] | null | undefined
): TopologyViewModel {
  const safeHubs = hubs ?? [];
  const safeDevices = devices ?? [];

  const viewDevices: TopologyDevice[] = safeDevices
    .filter((d): d is ApiTopologyDevice & { hub_id: string } => d.hub_id != null)
    .map((d) => {
      const linkSpeedBucket = bucketLinkSpeed(d.link_speed, d.status);
      const lowConfidenceMatch =
        d.match_confidence != null && LOW_CONFIDENCE.has(d.match_confidence);
      return {
        id: d.id,
        serial: d.serial,
        model: d.model,
        status: d.status,
        hubId: d.hub_id,
        linkSpeedRaw: d.link_speed,
        linkSpeedBucket,
        isOffline: d.status === "offline",
        isDegraded: linkSpeedBucket === "full",
        lowConfidenceMatch,
        matchConfidence: d.match_confidence,
      };
    });

  const viewHubs: TopologyHub[] = safeHubs.map((h) => ({
    id: h.id,
    name: h.name ?? h.location_id,
    locationId: h.location_id,
    uplinkHubId: h.uplink_hub_id,
    health: mapHubHealth(h.health),
    healthRaw: h.health,
    deviceCount: h.usb_device_count,
    deviceIds: h.device_ids,
    lastSeenAt: h.last_seen_at,
  }));

  const offlineCount = viewDevices.filter((d) => d.isOffline).length;
  const degradedLinkCount = viewDevices.filter((d) => d.isDegraded).length;

  return {
    hubs: viewHubs,
    devices: viewDevices,
    kpis: {
      deviceCount: viewDevices.length,
      onlineCount: viewDevices.length - offlineCount,
      offlineCount,
      hubCount: viewHubs.length,
      degradedLinkCount,
    },
  };
}
